import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Trash2, FileText, Mail } from "lucide-react";
import { FileAzioni } from "./FileViewer";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Tiles } from "./SmartUI";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const pad = (n) => String(n).padStart(2, "0");
const oggiIso = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const scadenzaDi = (anno, mese) => (mese === 12 ? `${anno + 1}-01-15` : `${anno}-${pad(mese + 1)}-15`);
const fmtIt = (iso) => iso.split("-").reverse().join("/");

export function statoBusta(anno, mese, busta, assunzione) {
  const ym = `${anno}-${pad(mese)}`; const oggi = oggiIso();
  if (assunzione && ym < assunzione.slice(0, 7)) return { k: "prima", l: "Prima dell'assunzione", c: "#94a3b8", sub: "non dovuta" };
  if (ym > oggi.slice(0, 7)) return { k: "futuro", l: "", c: "#cbd5e1", sub: "" };
  if (busta) return { k: "ok", l: "Caricata", c: "#059669", sub: "" };
  const sc = scadenzaDi(anno, mese);
  if (ym === oggi.slice(0, 7)) return { k: "corso", l: "Mese in corso", c: "#64748b", sub: `da caricare entro il ${fmtIt(sc)}` };
  if (!assunzione) return { k: "manca", l: "Da caricare", c: "#d97706", sub: `scadenza ${fmtIt(sc)}` };
  return oggi > sc ? { k: "ritardo", l: "In ritardo", c: "#dc2626", sub: `doveva arrivare entro il ${fmtIt(sc)}` } : { k: "attesa", l: "Da caricare", c: "#d97706", sub: `entro il ${fmtIt(sc)}` };
}

export function calcRitardo(list, assunzione) {
  const tutte = (list || []).filter((b) => typeof b.mese === "number" && b.anno);
  const set = new Set(tutte.map((b) => `${b.anno}-${pad(b.mese)}`));
  const ritardo = []; const dovuti = [];
  if (assunzione) {
    const oggi = oggiIso(); let [y, m] = assunzione.slice(0, 7).split("-").map(Number); let g = 0;
    while (`${y}-${pad(m)}` <= oggi.slice(0, 7) && g++ < 600) {
      const s = statoBusta(y, m, set.has(`${y}-${pad(m)}`), assunzione);
      if (s.k !== "corso") dovuti.push([y, m]);
      if (s.k === "ritardo") ritardo.push([y, m]);
      m += 1; if (m > 12) { m = 1; y += 1; }
    }
  }
  return { tutte, ritardo, dovuti };
}

export function BusteTiles({ assunzione, tutte, ritardo, dovuti }) {
  if (!assunzione) return null;
  const now = new Date(); const y = now.getFullYear(); const m = now.getMonth() + 1; const [py, pm] = m === 1 ? [y - 1, 12] : [y, m - 1];
  const has = (a, b) => tutte.some((x) => x.anno === a && x.mese === b);
  const prossima = statoBusta(py, pm, has(py, pm), assunzione).k === "attesa" ? [scadenzaDi(py, pm), `busta di ${MESI[pm - 1].toLowerCase()}`]
    : statoBusta(y, m, has(y, m), assunzione).k === "corso" ? [scadenzaDi(y, m), `busta di ${MESI[m - 1].toLowerCase()}`] : null;
  return (
    <div className="mb-4">
      <Tiles testid="buste-tiles" items={[
        ["Caricate", tutte.length, `dal ${fmtIt(assunzione).slice(3)}`, "#059669"],
        ["In ritardo", <span data-testid="buste-ritardo-n">{ritardo.length}</span>, ritardo.length ? ritardo.slice(0, 3).map(([a, b]) => `${MESI[b - 1].slice(0, 3)} ${a}`).join(", ") : "nessuna", "#dc2626", ritardo.length > 0],
        ["Prossima scadenza", prossima ? fmtIt(prossima[0]).slice(0, 5) : "—", prossima ? prossima[1] : "", "#d97706"],
        ["Mesi dovuti", dovuti.length, `da ${MESI[Number(assunzione.slice(5, 7)) - 1].toLowerCase()} ${assunzione.slice(0, 4)}`, "#475569"],
      ]} />
      <p className="text-[11px] text-muted-foreground mt-1.5">Ogni busta paga va caricata entro il 15 del mese successivo al mese lavorato.</p>
    </div>
  );
}

export function useBusteRitardo(operatoreId, assunzione, enabled = true) {
  const { data = [] } = useQuery({ enabled: !!operatoreId && enabled, queryKey: ["buste", operatoreId, "tutte"], retry: false, queryFn: async () => (await api.get("/buste-paga", { params: { operatore_id: operatoreId } })).data });
  return calcRitardo(data, assunzione);
}

const MONTH_WORDS = [["gennaio", "genn", "gen"], ["febbraio", "febb", "feb"], ["marzo", "mar"], ["aprile", "apr"], ["maggio", "magg", "mag"], ["giugno", "giug", "giu"], ["luglio", "lugl", "lug"], ["agosto", "agos", "ago"], ["settembre", "sett", "set"], ["ottobre", "otto", "ott"], ["novembre", "nove", "nov"], ["dicembre", "dice", "dic"]];
function detectMonth(fn) {
  const s = (fn || "").toLowerCase();
  for (let i = 0; i < 12; i++) { if (MONTH_WORDS[i].some((w) => s.includes(w))) return i + 1; }
  let m = s.match(/(20\d{2})[^0-9]?(0?[1-9]|1[0-2])(?![0-9])/); if (m) return parseInt(m[2], 10);
  m = s.match(/(0?[1-9]|1[0-2])[^0-9]?(20\d{2})/); if (m) return parseInt(m[1], 10);
  m = s.match(/(?:^|[^0-9])(0[1-9]|1[0-2])(?:[^0-9]|$)/); if (m) return parseInt(m[1], 10);
  return null;
}
function detectYear(fn) { const m = (fn || "").match(/20\d{2}/); return m ? parseInt(m[0], 10) : null; }

export default function BustePaga({ operatoreId, operatoreNome, structureId, dataAssunzione, cooperativa }) {
  const qc = useQueryClient();
  const now = new Date();
  const assunzione = cooperativa ? "" : (dataAssunzione || "").slice(0, 10);
  const stato = (y, m, b) => (cooperativa ? (b ? { k: "ok", l: "Caricata", c: "#059669", sub: "" } : { k: "libero", l: "", c: "#cbd5e1", sub: "" }) : statoBusta(y, m, b, assunzione));
  const annoAss = assunzione ? Number(assunzione.slice(0, 4)) : null;
  const [anno, setAnno] = useState(now.getFullYear());
  const [busy, setBusy] = useState(false);
  const fileRefs = useRef({});
  const multiRef = useRef(null);
  const { tutte, ritardo, dovuti } = useBusteRitardo(operatoreId, assunzione);

  const { data: anni = [] } = useQuery({ queryKey: ["buste-anni", operatoreId], queryFn: async () => (await api.get("/buste-paga/anni", { params: { operatore_id: operatoreId } })).data });
  const { data: buste = [] } = useQuery({ queryKey: ["buste", operatoreId, anno], queryFn: async () => (await api.get("/buste-paga", { params: { operatore_id: operatoreId, anno } })).data });
  const byMese = {}; buste.forEach((b) => { byMese[b.mese] = b; });
  const anniBase = annoAss && annoAss <= now.getFullYear() ? Array.from({ length: now.getFullYear() - annoAss + 1 }, (_, i) => annoAss + i) : [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
  const anniOptions = Array.from(new Set([...anni, ...anniBase, now.getFullYear()])).sort((a, b) => b - a);
  const has = (y, m) => tutte.some((b) => b.anno === y && b.mese === m);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["buste"] }); qc.invalidateQueries({ queryKey: ["buste-anni"] }); qc.invalidateQueries({ queryKey: ["op-conformita"] }); };

  const upload = async (mese, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("operatore_id", operatoreId); fd.append("operatore_nome", operatoreNome || "");
    fd.append("anno", anno); fd.append("mese", mese); if (structureId) fd.append("structure_id", structureId);
    try {
      await api.post("/buste-paga/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Busta paga caricata"); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante il caricamento"); }
  };
  const del = async (b) => { try { await api.delete(`/buste-paga/${b.id}`); toast.success("Eliminata"); refresh(); } catch { toast.error("Errore"); } };
  const [emailBusy, setEmailBusy] = useState(null);
  const inviaEmail = async (b) => {
    setEmailBusy(b.id);
    try { const r = await api.post(`/buste-paga/${b.id}/invia-email`); toast.success(`Busta paga inviata a ${r.data.email}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Invio email non riuscito"); }
    setEmailBusy(null);
  };

  const handleMulti = async (files) => {
    const arr = Array.from(files || []); if (!arr.length) return;
    setBusy(true);
    let ok = 0; const skipped = []; const prima = [];
    for (const f of arr) {
      const mese = detectMonth(f.name); const y = detectYear(f.name) || anno;
      if (!mese) { skipped.push(f.name); continue; }
      if (statoBusta(y, mese, null, assunzione).k === "prima") { prima.push(f.name); continue; }
      const fd = new FormData();
      fd.append("file", f); fd.append("operatore_id", operatoreId); fd.append("operatore_nome", operatoreNome || "");
      fd.append("anno", y); fd.append("mese", mese); if (structureId) fd.append("structure_id", structureId);
      try { await api.post("/buste-paga/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; } catch { skipped.push(f.name); }
    }
    setBusy(false); refresh();
    if (ok) toast.success(`${ok} buste caricate automaticamente`);
    if (skipped.length) toast.error(`Mese non riconosciuto: ${skipped.join(", ")}`);
    if (prima.length) toast.error(`Mese precedente all'assunzione: ${prima.join(", ")}`);
    if (multiRef.current) multiRef.current.value = "";
  };

  return (
    <div>
      {cooperativa ? <div className="mb-4 rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="buste-cooperativa">Assunto/a con <b>cooperativa</b>: le buste paga non sono obbligatorie. Puoi comunque caricarle se vuoi.</div>
        : assunzione ? <BusteTiles assunzione={assunzione} tutte={tutte} ritardo={ritardo} dovuti={dovuti} /> : <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="buste-no-assunzione">Imposta la <b>data di assunzione</b> dell'operatore (Modifica) per controllare le buste mancanti e in ritardo.</div>}
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm font-semibold">Anno</label>
        <select value={anno} onChange={(e) => setAnno(Number(e.target.value))} className="h-10 rounded-md border px-3 text-sm font-semibold" data-testid="buste-anno-select">
          {anniOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{buste.length} buste nel {anno}</span>
      </div>
      <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); handleMulti(e.dataTransfer.files); }}
        className="mb-4 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 p-4 text-center" data-testid="buste-dropzone">
        <input ref={multiRef} type="file" accept=".pdf,image/*" multiple className="hidden" data-testid="buste-multi-file" onChange={(e) => handleMulti(e.target.files)} />
        <div className="text-sm text-muted-foreground">Trascina qui più buste insieme — il mese viene riconosciuto dal nome del file (es. "gennaio", "2026-03").</div>
        <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => multiRef.current?.click()} data-testid="buste-multi-btn">
          <Upload size={14} className="mr-1" /> {busy ? "Caricamento..." : "Carica più mesi"}
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {MESI.map((nome, idx) => {
          const mese = idx + 1; const b = byMese[mese]; const s = stato(anno, mese, b); const off = s.k === "prima" || s.k === "futuro";
          return (
            <div key={mese} className={`rounded-xl border-2 bg-white p-3 flex items-center justify-between gap-2 ${off ? "opacity-60" : ""}`} style={{ borderColor: `${s.c}55` }} data-testid={`busta-mese-${mese}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={18} style={{ color: s.c }} className="shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">{nome}{s.l && <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5" style={s.k === "ritardo" ? { background: s.c, color: "#fff" } : { background: `${s.c}18`, color: s.c }} data-testid={`busta-stato-${mese}`}>{s.l}</span>}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{b ? (b.original_filename || "caricata") : (s.sub || "Nessuna busta")}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {b ? (
                  <>
                    <FileAzioni url={`/buste-paga/${b.id}/download`} titolo={b.original_filename || `busta_${anno}_${b.mese}.pdf`} testid={`busta-${mese}`} size={16} />
                    <button onClick={() => inviaEmail(b)} disabled={emailBusy === b.id} title="Invia via email all'operatore" data-testid={`busta-email-${mese}`} className="p-1.5 rounded hover:bg-secondary text-primary disabled:opacity-50"><Mail size={16} /></button>
                    <button onClick={() => del(b)} title="Elimina" data-testid={`busta-del-${mese}`} className="p-1.5 rounded hover:bg-secondary text-destructive"><Trash2 size={16} /></button>
                  </>
                ) : off ? null : (
                  <>
                    <input ref={(el) => (fileRefs.current[mese] = el)} type="file" accept=".pdf,image/*" className="hidden" data-testid={`busta-file-${mese}`} onChange={(e) => upload(mese, e.target.files?.[0])} />
                    <Button size="sm" variant={s.k === "ritardo" ? "default" : "outline"} className="h-8 text-xs" onClick={() => fileRefs.current[mese]?.click()} data-testid={`busta-upload-${mese}`}><Upload size={14} className="mr-1" /> Carica</Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
